import { useCallback } from "react";
import { graphql, useMutation } from "react-relay";

import { useTodoEditResponseImageGenerateUploadLinkMutation } from "./__generated__/useTodoEditResponseImageGenerateUploadLinkMutation.graphql";
import { useTodoEditResponseImageMutation } from "./__generated__/useTodoEditResponseImageMutation.graphql";
import { useUploadImage } from "arkh";

export function useTodoEditResponseImage(props: { todoId: string }) {
  const [mutation] = useMutation<useTodoEditResponseImageMutation>(graphql`
    mutation useTodoEditResponseImageMutation(
      $todoId: ID!
      $input: UpdateTodoImageResponseInput!
    ) {
      todo(todoId: $todoId) {
        updateImageResponse(input: $input) {
          id
          isResponseCompleted
          canChangeStatus
          parent {
            canChangeStatus
          }
          response {
            ... on TodoResponseImage {
              images {
                id
                ...Image_image
              }
            }
          }
        }
      }
    }
  `);

  const [generateUploadLink] =
    useMutation<useTodoEditResponseImageGenerateUploadLinkMutation>(graphql`
      mutation useTodoEditResponseImageGenerateUploadLinkMutation {
        uploadImageLink {
          image {
            id
            ...Image_image
          }
          url
        }
      }
    `);

  const { uploadImage, prepareForImageUpload } = useUploadImage();

  const addImage = useCallback(
    async (
      uri: string,
      { width, height }: { width: number; height: number }
    ) => {
      const uploadIdRequest = prepareForImageUpload({
        width,
        height,
        localImageURI: uri,
      });

      generateUploadLink({
        variables: {},
        onCompleted: (data) => {
          uploadIdRequest.then((uploadId) => {
            if (data.uploadImageLink) {
              const id = data.uploadImageLink.image.id;
              uploadImage({
                imageId: id,
                uploadId,
                uploadURL: data.uploadImageLink.url,
              });
              mutation({
                variables: {
                  todoId: props.todoId,
                  input: {
                    addImageIds: [id],
                  },
                },
              });
            }
          });
        },
      });
    },
    [
      generateUploadLink,
      mutation,
      prepareForImageUpload,
      props.todoId,
      uploadImage,
    ]
  );

  const removeImage = useCallback(
    (id: string) => {
      mutation({
        variables: {
          todoId: props.todoId,
          input: {
            removeImageIds: [id],
          },
        },
      });
    },
    [mutation, props.todoId]
  );

  return { addImage, removeImage };
}
